import { useEffect, useState } from 'react';

import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { PresentationSlideProps } from '@/modules/presentation/PresentationSlide/PresentationSlide';
import { diagnostics } from '@/utils/diagnostics';

const DEFAULT_TIMEOUT = 100;

export interface DataTableSlideProps extends PresentationSlideProps {
  Slide?: typeof PresentationSlide.Slide;
  timeout?: number;
  slideTitle?: string;
}

/**
 * Wraps a slide with a DataTable in a slide.
 *
 * This is a workaround to prevent the table from being rendered before the slide has been scaled
 * to its proper width; if it's rendered before that, the table will have visual errors.
 */
export function DataTableSlide({
  children,
  id,
  slideTitle,
  timeout = DEFAULT_TIMEOUT,
  leftHeaderContent,
  Slide = PresentationSlide.Slide,
  footer = Slide === PresentationSlide.Slide ? (
    <PresentationSlide.Footer />
  ) : undefined,
  ...props
}: DataTableSlideProps) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), timeout);
    return () => clearTimeout(timer);
  }, [timeout]);

  if (!leftHeaderContent) {
    if (slideTitle) {
      leftHeaderContent = (
        <PresentationSlide.MainHeading heading={slideTitle} />
      );
    } else {
      diagnostics.warn('No slide title or left header content provided', {
        id,
      });
    }
  }

  return (
    <Slide
      {...props}
      id={id}
      leftHeaderContent={leftHeaderContent}
      footer={footer}
      data-presentationdatatableready={isReady.toString()}
    >
      {isReady ? children : null}
    </Slide>
  );
}
